import React, { useCallback, useState } from 'react';

import PropTypes from 'prop-types';
import { Formik, Form } from 'formik';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { InputAdornment } from '@mui/material';

import GenericErrorMessage from '../../../../../components/GenericErrorMessage';
import useComponentMounted from '../../../../../hooks/useComponentMounted';
import useNavigation from '../../../../../hooks/useNavigation';
import SubmitButton from '../SubmitButton';
import FormikLabeledInput from '../Input';
import FormikPhoneInput from '../../../../../components/FormikInput/FormikPhoneInput';

import {
  Container,
  SignUpContent,
  ErrorContainer,
  StyledLinkButton,
  NameContainer,
  StyledTitle,
  HelperTextContainer,
  StyledLockIcon,
  StyledIconButton,
  PersonalDataInputContainer,
} from './styles';

import { getInitialValues, validationSchema } from './validation';
import texts from './texts.json';

const AccountCreation = ({
  email,
  firstName,
  lastName,
  phoneNumber,
  onSubmit,
  state,
  color,
  isDarkTheme,
}) => {
  const {
    isError,
    errorMsg,
    route,
    routeSearch,
    routeState,
  } = state;

  const [showPassword, setShowPassword] = useState(false);

  const handleTogglePasswordVisibility = () => {
    setShowPassword((prevShowPassword) => !prevShowPassword);
  };

  const isComponentMountedRef = useComponentMounted();
  const { navigateTo } = useNavigation();

  const handleSubmit = useCallback(async (values, actions) => {
    await onSubmit(values);
    if (isComponentMountedRef.current) {
      actions.setSubmitting(false);
    }
  }, [
    onSubmit,
    isComponentMountedRef,
  ]);

  const handleNavigate = useCallback(() => {
    navigateTo(route, { search: routeSearch, state: routeState });
  }, [
    navigateTo,
    route,
    routeState,
    routeSearch,
  ]);

  return (
    <Formik
      initialValues={getInitialValues({
        email,
        firstName,
        lastName,
        phoneNumber,
      })}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ isSubmitting }) => (
        <Form>
          <Container>
            <SignUpContent>
              <StyledTitle $isDarkTheme={isDarkTheme}>
                {texts.personalDetails}
              </StyledTitle>
              <PersonalDataInputContainer>
                <NameContainer>
                  <FormikLabeledInput
                    name="firstName"
                    type="text"
                    autoComplete="first-name"
                    label={texts.label.firstName}
                    isHalfWidth
                    isDarkTheme={isDarkTheme}
                  />
                  <FormikLabeledInput
                    name="lastName"
                    type="text"
                    autoComplete="last-name"
                    label={texts.label.lastName}
                    isHalfWidth
                    isDarkTheme={isDarkTheme}
                  />
                </NameContainer>
                <FormikPhoneInput
                  name="phoneNumber"
                  label={texts.label.phoneNumber}
                  isDarkTheme={isDarkTheme}
                  autoComplete="tel"
                  className="onboarding-phone-input"
                />
              </PersonalDataInputContainer>
              <StyledTitle $isDarkTheme={isDarkTheme}>
                {texts.loginDetails}
              </StyledTitle>
              <FormikLabeledInput
                name="email"
                autoComplete="email"
                label={texts.label.email}
                placeholder={texts.placeholder.email}
                isDarkTheme={isDarkTheme}
              />
              <FormikLabeledInput
                name="password"
                placeholder={texts.placeholder.password}
                label={texts.label.password}
                type={showPassword ? 'text' : 'password'}
                isDarkTheme={isDarkTheme}
                endAdornment={(
                  <InputAdornment position="end">
                    <StyledIconButton
                      aria-label="toggle password visibility"
                      onClick={handleTogglePasswordVisibility}
                      size="small"
                    >
                      {showPassword ? <Visibility /> : <VisibilityOff />}
                    </StyledIconButton>
                  </InputAdornment>
                )}
              />
              <HelperTextContainer>
                <StyledLockIcon />
                {texts.securePassword}
              </HelperTextContainer>
              <SubmitButton
                isLoading={isSubmitting}
                type="submit"
                $background={color}
                $isDarkTheme={isDarkTheme}
              >
                {texts.createAccount}
              </SubmitButton>
              { isError && (
                <ErrorContainer>
                  <GenericErrorMessage>{errorMsg}</GenericErrorMessage>
                  { route && (
                    <StyledLinkButton
                      onClick={handleNavigate}
                      $isDarkTheme={isDarkTheme}
                    >
                      {texts.loginWithEmail}
                    </StyledLinkButton>
                  )}
                </ErrorContainer>
              )}
            </SignUpContent>
          </Container>
        </Form>
      )}
    </Formik>
  );
};

AccountCreation.propTypes = {
  email: PropTypes.string,
  firstName: PropTypes.string,
  lastName: PropTypes.string,
  phoneNumber: PropTypes.string,
  onSubmit: PropTypes.func,
  state: PropTypes.object,
  color: PropTypes.string,
  isDarkTheme: PropTypes.bool.isRequired,
};

AccountCreation.defaultProps = {
  email: '',
  firstName: '',
  lastName: '',
  phoneNumber: '',
  onSubmit: () => {},
  state: {},
  color: '',
};

export default AccountCreation;
